import { Schema } from "@effect/schema";
import { getLastUpdated, ProtocolKey, StaticMarket } from "@silo/types";
import { Config, Duration, Effect, pipe, Redacted } from "effect";
import { toFormattedJson } from "../utils";
import { getProtocolStaticMarketsFileName } from "./utils";
import { BlobClientError, BlobService, StaticMarketsFile } from "./types";

const FIFTY_FIVE_MINUTES = pipe(Duration.minutes(55), Duration.toSeconds);

export type SetProtocolStaticMarkets = ReturnType<
  typeof setProtocolStaticMarkets
>;
export type SetProtocolStaticMarketsSuccess =
  Effect.Effect.Success<SetProtocolStaticMarkets>;
export type SetProtocolStaticMarketsError =
  Effect.Effect.Error<SetProtocolStaticMarkets>;
export type SetProtocolStaticMarketsContext =
  Effect.Effect.Context<SetProtocolStaticMarkets>;

export function setProtocolStaticMarkets(
  protocolKey: ProtocolKey,
  staticMarkets: StaticMarket[]
) {
  return Effect.gen(function* () {
    const staticMarketsFile: StaticMarketsFile = {
      $lastUpdated: getLastUpdated(),
      data: staticMarkets,
    };

    const json = yield* pipe(
      staticMarketsFile,
      Schema.encode(StaticMarketsFile),
      Effect.andThen(toFormattedJson)
    );

    const fileName = yield* getProtocolStaticMarketsFileName(protocolKey);

    const BLOB_READ_WRITE_TOKEN = yield* Config.redacted(
      "BLOB_READ_WRITE_TOKEN"
    );

    const blob = yield* BlobService;

    const result = yield* Effect.tryPromise({
      try: () => {
        return blob.put(fileName, json, {
          access: "public",
          contentType: "application/json",
          token: Redacted.value(BLOB_READ_WRITE_TOKEN),
          addRandomSuffix: false,
          cacheControlMaxAge: FIFTY_FIVE_MINUTES,
          // cacheControlMaxAge: 0,
        });
      },
      catch: (cause) => new BlobClientError({ cause }),
    });

    yield* Effect.log(`Updated static markets at ${result.url}`);
    yield* Effect.log(`Count of markets: ${staticMarkets.length}`);

    return result;
  });
}
